class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resultsContainer = this.querySelector('.search-results');
    if (this.dataset.main === 'true') {
      const serialEl = this.querySelector('.search-serial');
      if (serialEl?.dataset.serial) serial = serialEl.dataset.serial.replace('n', 'm');
    }
    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.handleSearchInput(event);
        }, 300).bind(this),
      );
    }
  }
  toggleResultsVisibility() {
    const isHidden = this.resultsContainer.classList.contains('hidden');
    if (this.input.value.length > 0 && isHidden) {
      this.resultsContainer.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !isHidden) {
      this.resultsContainer.classList.add('hidden');
    }
  }
  handleSearchInput() {
    this.toggleResultsVisibility();
  }
  shouldResetForm() {
    return !document.querySelector('.search-active');
  }
  onFormReset(event) {
    event.preventDefault();
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.blur();
      this.toggleResultsVisibility();
    }
  }
}
customElements.define('search-form', SearchForm);
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.resultsContainer = this.querySelector('[data-predictive-search]');
    this.instances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.setupEventListeners();
  }
  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('input', this.handleSearchInput.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('focusin', this.onFocusIn.bind(this));
  }
  getSearchTerm() {
    return this.input.value.trim();
  }
  handleSearchInput() {
    super.handleSearchInput();
    const newSearchTerm = this.getSearchTerm();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('.predictive-search-status')?.classList.remove('hidden');
    }
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;
    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }
    this.renderSearchResults(this.searchTerm);
  }
  onFormSubmit(event) {
    if (!this.getSearchTerm().length || this.querySelector("[aria-selected='true'] a")) {
      event.preventDefault();
    }
  }
  onFocusIn() {
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.close(true);
    }
  }
  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }
  onKeydown(event) {
    if (!this.getSearchTerm().length) {
      this.close(true);
    }
    // Only prevent default for navigation keys; otherwise typing into the
    // search input is blocked (every keydown would be canceled).
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }
  switchOption(direction) {
    if (!this.hasAttribute('results')) return;
    const isMovingUp = direction === 'up';
    const currentSelected = this.querySelector("[aria-selected='true']");
    const options = Array.from(this.querySelectorAll('[role="option"]')).filter(
      (option) => option.offsetParent !== null,
    );
    let currentIndex = 0;
    if (isMovingUp && !currentSelected) return;
    let newIndex = -1;
    let counter = 0;
    while (newIndex === -1 && counter <= options.length) {
      if (options[counter] === currentSelected) {
        newIndex = counter;
      }
      counter++;
    }
    this.removeAttribute('aria-activedescendant');
    if (!isMovingUp && currentSelected) {
      currentIndex = newIndex === options.length - 1 ? 0 : newIndex + 1;
    } else if (isMovingUp) {
      currentIndex = newIndex === 0 ? options.length - 1 : newIndex - 1;
    }
    if (currentIndex === newIndex) return;
    const newSelected = options[currentIndex];
    newSelected.setAttribute('aria-selected', true);
    if (currentSelected) {
      currentSelected.setAttribute('aria-selected', false);
    }
    this.input.setAttribute('aria-activedescendant', newSelected.id);
  }
  selectOption() {
    const selectedOption = this.querySelector("[aria-selected='true'] a");
    if (selectedOption) selectedOption.click();
  }
  renderSearchResults(searchTerm) {
    const formattedTerm = searchTerm.replace(' ', '-').toLowerCase();
    this.setLoadingState();
    if (this.cachedResults[formattedTerm]) {
      this.renderResults(this.cachedResults[formattedTerm]);
      return;
    }
    fetch(
      routes.predictive_search_url +
        `?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
      {
        signal: this.abortController.signal,
      },
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const resultsEl =
          doc.querySelector('#predictive-search-results') ||
          doc.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = resultsEl ? resultsEl.outerHTML : '';
        this.instances.forEach((instance) => {
          instance.cachedResults[formattedTerm] = resultsMarkup;
        });
        this.renderResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) return;
        this.close();
        throw error;
      });
  }
  setLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }
  setLiveRegionText(text) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.innerText = text;
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }
  renderResults(html) {
    this.resultsContainer.innerHTML = html;
    this.setAttribute('results', true);
    this.updateLiveRegions();
    this.openResults();
  }
  openResults() {
    this.resultsContainer.style.maxHeight = this.getResultsMaxHeight() + 'px';
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }
  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      (document.querySelector('.section-header')?.getBoundingClientRect().bottom ??
        this.getBoundingClientRect().bottom);
    return this.resultsMaxHeight;
  }
  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g'))?.length > 1) return;
      searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
    }
  }
  updateLiveRegions() {
    const liveRegion = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegion) {
      liveRegion.textContent = this.querySelectorAll(
        '#predictive-search-results [role="option"]',
      ).length;
    }
  }
  close(reset = false) {
    this.closeResults(reset);
    this.isOpen = false;
  }
  closeResults(reset = false) {
    if (reset) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selectedOption = this.querySelector("[aria-selected='true']");
    if (selectedOption) {
      selectedOption.setAttribute('aria-selected', false);
    }
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.input.setAttribute('aria-expanded', false);
    this.resultsContainer.removeAttribute('style');
  }
}
customElements.define('predictive-search', PredictiveSearch);
